import React from 'react';
import { Target, Store, DollarSign, LineChart, FlaskConical, Layers } from 'lucide-react';

const blocks = [
  {
    icon: Target,
    title: 'Acquisition Strategy',
    body: "We identify the best channels, audiences, markets, budgets, and campaign structure for your app's growth stage.",
    includes: ['Meta Ads', 'Google App Campaigns', 'TikTok Ads', 'Apple Search Ads', 'YouTube', 'Retargeting & lifecycle'],
  },
  {
    icon: Store,
    title: 'App Store Conversion',
    body: 'We improve the store page and pre-install journey so more qualified traffic becomes users.',
    includes: ['Screenshot messaging', 'Video / preview creative', 'Review & rating signals', 'Keyword / ASO', 'Creative-to-store consistency'],
  },
  {
    icon: DollarSign,
    title: 'Revenue Growth',
    body: 'We focus on users who generate business value, not just low-cost installs.',
    includes: ['Subscription funnel analysis', 'IAP conversion', 'Trial-to-paid optimization', 'Retention & reactivation', 'LTV / CAC thinking'],
  },
  {
    icon: LineChart,
    title: 'Measurement & Tracking',
    body: 'We help make campaign performance easier to trust and act on.',
    includes: ['GA4 / Firebase', 'AppsFlyer / Adjust', 'SKAN measurement logic', 'Campaign dashboards', 'Event tracking & funnel reporting'],
  },
  {
    icon: FlaskConical,
    title: 'Creative Testing',
    body: 'We create a repeatable testing system around angles, audiences, value propositions, hooks, screenshots, and landing/app-store messaging.',
    includes: ['Angle discovery', 'Structured variants', 'Store-page alignment', 'Winner scaling', 'Fatigue detection'],
  },
  {
    icon: Layers,
    title: 'Growth Prioritization',
    body: 'We help your team decide what to test first, what to pause, and where growth effort is most likely to pay back.',
    includes: ['Impact vs. effort mapping', 'Confidence scoring', 'Sequencing', 'Roadmap alignment', 'Stakeholder communication'],
  },
];

const loopStages = ['Traffic', 'Store Page', 'Install', 'Activation', 'Revenue', 'Retention', 'Learning'];

const Solution = () => {
  return (
    <section
      data-testid="app-growth-solution"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-white"
    >
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mb-14">
          <div className="inline-flex items-center gap-2 text-blue-600 text-sm font-semibold uppercase tracking-wider mb-4">
            The Upscale approach
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight tracking-tight">
            We build app growth <em className="not-italic text-blue-600">systems</em>,
            not isolated campaigns.
          </h2>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            Upscale combines performance marketing, analytics, creative testing, and conversion
            optimization to help app companies acquire better users and turn more of them into
            paying customers. We look at the full path:
          </p>
          <p className="mt-4 text-base text-gray-900 font-semibold leading-relaxed">
            Ad impression → App Store visit → install → onboarding → activation → trial or purchase → repeat use → revenue.
          </p>
        </div>

        {/* Growth Loop visual */}
        <div className="mb-16 py-10 px-4 bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl overflow-x-auto">
          <div className="text-white text-center text-xs uppercase tracking-[0.2em] mb-6 opacity-70">
            The Upscale Growth Loop
          </div>
          <div className="flex items-center justify-between min-w-[720px] max-w-5xl mx-auto px-4">
            {loopStages.map((stage, i) => (
              <React.Fragment key={stage}>
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 rounded-full bg-white/10 border-2 border-blue-400/60 backdrop-blur flex items-center justify-center text-blue-400 font-bold">
                    {i + 1}
                  </div>
                  <div className="text-white text-sm mt-3 font-medium">{stage}</div>
                </div>
                {i < loopStages.length - 1 && (
                  <div className="flex-1 h-0.5 bg-gradient-to-r from-blue-400/40 via-white/30 to-blue-400/40 mx-2 relative">
                    <div className="absolute right-0 -top-1 w-2 h-2 border-r-2 border-t-2 border-white/40 rotate-45" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* 6 growth system blocks */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {blocks.map((b, i) => {
            const Icon = b.icon;
            return (
              <div
                key={i}
                data-testid={`growth-block-${i}`}
                className="bg-white border border-gray-100 rounded-2xl p-7 hover:border-blue-200 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                    <Icon size={18} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{b.title}</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{b.body}</p>
                <ul className="space-y-1.5">
                  {b.includes.map((it, j) => (
                    <li key={j} className="text-sm text-gray-700 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-600 rounded-full flex-shrink-0" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Solution;
